import { createWebPortalRecordRequestSchema, RBIM_QUESTIONNAIRE_PAYLOAD_SCHEMA_VERSION } from "@rbim/shared";
import { apiRequest } from "../../api";
import { authoritativeHouseholdLocation } from "../rbim-questionnaire/questionnaire-model";
import { withCurrentManilaDateEncoded } from "./date-encoded";
import { cleanRBIMCode } from "./lib/rbimCodeMap";
import { addNewDataSchema, defaultAddNewDataValues, emptyMember, type AddNewDataFormValues } from "./modules/add-new-data/lib/schema";
import { applySkipPattern, cleanAddNewDataValues } from "./modules/add-new-data/lib/cleansing";
import type { WebWorkspaceProps } from "./web-adapter";

type SubmissionContext = Pick<WebWorkspaceProps, "barangayId" | "barangays" | "context" | "recordCreatePath" | "recordUpdatePath" | "onRecordSaved">;

const TIME_END_SUBMISSION_MESSAGE =
  "Please enter the Time End on the Front Page before submitting.";
const RESULT_SUBMISSION_MESSAGE =
  "Please enter the Result on the Front Page before submitting.";
const TIME_END_AND_RESULT_SUBMISSION_MESSAGE =
  "Please complete the Time End and Result on the Front Page before submitting.";

export function buildDesktopRecordRequest(values: AddNewDataFormValues, web: SubmissionContext) {
  const barangay = web.barangays.find((item) => item.id === web.barangayId && item.municipalityId === web.context.municipality.id);
  if (!barangay) throw new Error("Select an authorized Barangay before submitting.");

  assertInterviewCompletion(values);

  const valuesWithEncodedDate = web.recordUpdatePath ? values : withCurrentManilaDateEncoded(values);
  const located = {
    ...valuesWithEncodedDate,
    household: { ...valuesWithEncodedDate.household, ...authoritativeHouseholdLocation({ ...web.context, barangay }) },
    members: valuesWithEncodedDate.members.map(applySkipPattern),
  };

  // The Review step already runs the questionnaire's page-by-page validation
  // before persistence. Re-running that same step validator after replacing the
  // location with server-authoritative Barangay data can reject a Review that
  // was already accepted and leave the online Encoder unable to submit. Keep
  // the canonical Zod form schema and the shared API request schema as the two
  // final client boundaries; the server validates the request again.
  const parsed = addNewDataSchema.parse(located);
  const cleaned = cleanAddNewDataValues(parsed);
  const payload = {
    // These Desktop-only helper fields are intentionally excluded from the
    // strict shared Web API contract. They remain available in local form state
    // but must not be sent to Nodepoint.
    household: project(
      parsed.household,
      Object.keys(defaultAddNewDataValues.household),
      ["familyRelationshipToHouseholdHead"],
    ),
    householdQuestions: project(parsed.householdQuestions, Object.keys(defaultAddNewDataValues.householdQuestions)),
    interviews: parsed.interviews.map((item) => ({
      ...project(item, Object.keys(defaultAddNewDataValues.interviews[0]!)),
      // Some browsers/older autosaved drafts keep native date-input values as
      // YYYY-MM-DD. Nodepoint's canonical RBIM v1 API requires MM/DD/YYYY.
      // Normalize only at the Web API boundary so the draft is preserved and
      // the user can Retry Submission without re-encoding the record.
      dateOfVisit: normalizeWebCalendarDate(item.dateOfVisit),
      dateOfNextVisit: normalizeWebCalendarDate(item.dateOfNextVisit),
    })),
    encoding: {
      ...project(parsed.encoding, Object.keys(defaultAddNewDataValues.encoding)),
      dateEncoded: normalizeWebCalendarDate(parsed.encoding.dateEncoded),
    },
    members: parsed.members.map((member, index) => {
      const normalized = cleaned.members[index]!;
      const isHead = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead).code === "01";
      return {
        ...project(
          { ...emptyMember, ...member },
          Object.keys(emptyMember),
          ["residentId"],
        ),
        dateOfBirth: normalizeWebCalendarDate(member.dateOfBirth),
        birthdatePrecision: normalized.birthdatePrecision,
        dateOfBirthUnknown: normalized.dateOfBirthUnknown,
        reportedAge: normalized.reportedAge,
        ageAsOfDate: normalized.ageAsOfDate,
        ageSource: normalized.ageSource,
        familyUnitAssignment: normalized.familyUnitAssignment,
        familyUnitNo: normalized.familyUnitNo,
        familyName: normalized.familyName,
        isFamilyHead: isHead,
        relationshipToFamilyHead: isHead ? "Head" : member.relationshipToHead,
      };
    }),
  };
  return createWebPortalRecordRequestSchema.parse({ barangayId: barangay.id, payloadSchemaVersion: RBIM_QUESTIONNAIRE_PAYLOAD_SCHEMA_VERSION, payload });
}

export async function submitDesktopRecord(
  values: AddNewDataFormValues,
  web: SubmissionContext,
  sender: (path: string, init: RequestInit) => Promise<{ id: string }> = apiRequest,
) {
  const request = buildDesktopRecordRequest(values, web);
  const path = web.recordUpdatePath ?? web.recordCreatePath ?? "/records";
  const method = web.recordUpdatePath ? "PATCH" : "POST";
  const result = await sender(path, { method, body: JSON.stringify(request) });
  web.onRecordSaved?.(result.id);
  return { id: result.id, storage: "api" as const };
}

function assertInterviewCompletion(values: AddNewDataFormValues) {
  const timeEndMissing = !isValidSubmissionTimeEnd(values.interviews?.[0]?.timeEnd);
  const resultMissing = !hasSubmissionResult(values.interviews?.[0]?.result);

  if (!timeEndMissing && !resultMissing) return;

  if (timeEndMissing && resultMissing) {
    throw new Error(TIME_END_AND_RESULT_SUBMISSION_MESSAGE);
  }
  if (timeEndMissing) {
    throw new Error(TIME_END_SUBMISSION_MESSAGE);
  }
  throw new Error(RESULT_SUBMISSION_MESSAGE);
}

function isValidSubmissionTimeEnd(value: unknown) {
  const normalized = typeof value === "string" ? value.trim() : "";
  return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(normalized);
}

function hasSubmissionResult(value: unknown) {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizeWebCalendarDate(value: unknown) {
  const normalized = typeof value === "string" ? value.trim() : "";
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);

  if (!isoDate) return normalized;

  const [, year, month, day] = isoDate;
  return `${month}/${day}/${year}`;
}

function project(value: object, keys: string[], excludedKeys: string[] = []) {
  const record = value as Record<string, unknown>;
  const excluded = new Set(excludedKeys);
  return Object.fromEntries(
    keys
      .filter((key) => !excluded.has(key))
      .map((key) => [key, record[key]]),
  );
}
